import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';
import { types as coreTypes } from '@sitebuilder/core';
import { localization as commonLocalization } from '@sitebuilder/common';
import { ReferenceComponentApiClient } from '../api/referenceComponentsApiClient';
import { templateEditorReducerActions, travereTreeDepthFirst } from './templateEditor';
import { ThunkResult } from './index';

export interface StateShape {
    component: coreTypes.ReferenceComponent | null;
    isComponentLoaded: boolean;
    componentLoading: boolean;
    componentLoadError: any | null;
    componentSaveLoading: boolean;
    componentSaveSucess: string;
    componentSaveError: any | null;
    components: [coreTypes.ReferenceComponent] | null;
    componentsLoading: boolean;
    componentsLoadError: any | null;
    selectedComponents: string[];
    componentVersions: any[] | null;
    componentVersionsLoading: boolean;
    componentVersionsLoadError: any | null;
    revertedVersion: boolean;
    revertedVersionError: any | null;
    showDeletedItems: boolean;
    newFolderSaveLoading: boolean;
    newFolderSaveError: any | null;
    newFolderSaveSucess: string;
}

export const componentsDefaultState: StateShape = {
    component: null,
    isComponentLoaded: false,
    componentLoading: false,
    componentLoadError: null,
    componentSaveLoading: false,
    componentSaveSucess: '',
    componentSaveError: null,
    components: null,
    componentsLoading: false,
    componentsLoadError: null,
    selectedComponents: [],
    componentVersions: null,
    componentVersionsLoading: false,
    componentVersionsLoadError: null,
    revertedVersion: false,
    revertedVersionError: null,
    showDeletedItems: false,
    newFolderSaveLoading: false,
    newFolderSaveError: null,
    newFolderSaveSucess: '',
};

const apiClient = new ReferenceComponentApiClient();

export const actionCreators = {
    loadComponent: (
        libraryName: string,
        componentId: string,
        loadAfterEdit?: boolean
    ): ThunkResult<Promise<coreTypes.ReferenceComponent | undefined>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const accessToken = state.auth.user?.access_token;
            const isComponentEditor = state.templateEditor.editor === 'componentEditor';
            dispatch(componentsReducerActions.COMPONENT_LOAD_START());

            // show loading on component editor only after saving a component
            if (loadAfterEdit) {
                dispatch(componentsReducerActions.SET_IS_COMPONENT_LOADED(false));
            }

            try {
                const component = await apiClient.getComponent(libraryName, componentId, {
                    accessToken,
                });
                dispatch(componentsReducerActions.COMPONENT_LOAD_END({ component }));

                if (isComponentEditor && component) {
                    // When editing component in component editor
                    // consider component as document state
                    dispatch(templateEditorReducerActions.RECEIVE_DOCUMENT(component));
                    dispatch(componentsReducerActions.SET_IS_COMPONENT_LOADED(true));
                }

                return component;
            } catch (err: any) {
                console.error(err);
                dispatch(componentsReducerActions.SET_IS_COMPONENT_LOADED(true));
                dispatch(
                    componentsReducerActions.COMPONENT_LOAD_END({ error: { message: err.message } })
                );
            }
        };
    },
    saveComponent: (
        libraryName: string,
        data: coreTypes.SaveComponentFormData,
        widgetId: string,
        addNewComponent?: boolean,
        urlState?: string
    ): ThunkResult<Promise<boolean | void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const locale = state?.templateEditor?.documentMetadata?.locale || 'en-US';
            const appI18n = commonLocalization.getLocalization(locale);

            let component: coreTypes.ReferenceComponent;

            if (addNewComponent && widgetId === '') {
                component = {
                    id: '',
                    name: data.name,
                    description: data.description,
                    etag: '',
                    widgetInstances: {
                        root: {
                            widgetId: 'document',
                            parentId: '',
                            id: 'root',
                            properties: {},
                            contentPlaceHolders: {
                                children: [],
                            },
                        },
                    },
                };
            } else {
                const instancesIds = travereTreeDepthFirst(
                    state.templateEditor?.document?.widgetInstances as any,
                    widgetId as any
                );
                let widgetInstances = {};
                _.forEach(instancesIds, (id) => {
                    let root = state.templateEditor?.document?.widgetInstances?.root;

                    if (root) {
                        root = { ...root, contentPlaceHolders: { children: [widgetId] } };
                        widgetInstances = {
                            ...widgetInstances,
                            root,
                        };
                    }

                    if (id === widgetId) {
                        widgetInstances = {
                            ...widgetInstances,
                            [id]: {
                                ...state.templateEditor?.document?.widgetInstances[id],
                                parentId: 'root',
                            },
                        };
                    } else {
                        widgetInstances = {
                            ...widgetInstances,
                            root,
                            [id]: state.templateEditor?.document?.widgetInstances[id],
                        };
                    }
                });

                component = {
                    widgetInstances,
                    name: data.name,
                    description: data.description,
                    etag: '',
                    id: '',
                };
            }

            const accessToken = state.auth.user?.access_token;
            dispatch(componentsReducerActions.COMPONENT_SAVE_START());

            try {
                await apiClient.saveComponent(
                    libraryName,
                    urlState ? `${urlState.substring(1)}/${data.id}` : data.id,
                    component,
                    '',
                    {
                        accessToken,
                    }
                );
                dispatch(
                    componentsReducerActions.COMPONENT_SAVE_END({
                        success: appI18n.t('component_form_save_success'),
                    })
                );
                return true;
            } catch (err: any) {
                console.error(err);
                dispatch(
                    componentsReducerActions.COMPONENT_SAVE_END({
                        error: { message: err.message },
                    })
                );
                return false;
            }
        };
    },
    saveNewFolder: (folderName: string): ThunkResult<Promise<boolean | void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const locale = state?.templateEditor?.documentMetadata?.locale || 'en-US';
            const appI18n = commonLocalization.getLocalization(locale);

            const accessToken = state.auth.user?.access_token;
            dispatch(componentsReducerActions.NEWFOLDER_SAVE_START());

            try {
                await apiClient.saveNewFolder(folderName, {
                    accessToken,
                });
                dispatch(
                    componentsReducerActions.NEWFOLDER_SAVE_END({
                        success: appI18n.t('folder_form_save_success'),
                    })
                );
                return true;
            } catch (err: any) {
                console.error(err);
                dispatch(
                    componentsReducerActions.NEWFOLDER_SAVE_END({
                        error: { message: err.message },
                    })
                );
                return false;
            }
        };
    },
    editComponent: (
        libraryName: string,
        componentId: string,
        viewUrl: string
    ): ThunkResult<Promise<boolean>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const component: coreTypes.ReferenceComponent = state.templateEditor
                .document as coreTypes.ReferenceComponent;
            const accessToken = state.auth.user?.access_token;
            dispatch(componentsReducerActions.SET_IS_COMPONENT_LOADED(false));

            try {
                await apiClient.saveComponent(libraryName, componentId, component, viewUrl, {
                    accessToken,
                });
                return true;
            } catch (err: any) {
                console.error(err);
                return false;
            }
        };
    },
    listComponents: (
        libraryName: string,
        folderName: string,
        filters: { idPattern?: string[]; authorsIn?: Record<string, unknown> | [] } = {
            idPattern: [],
            authorsIn: [],
        },
        sort?: { field?: string; dir?: string },
        take?: number,
        skip?: number
    ): ThunkResult<Promise<void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const accessToken = state.auth.user?.access_token;
            dispatch(componentsReducerActions.COMPONENTS_LOAD_START());

            try {
                const components = await apiClient.listComponents(
                    libraryName,
                    folderName,
                    state.components.showDeletedItems,
                    {
                        accessToken,
                    },
                    filters,
                    sort,
                    take,
                    skip
                );
                dispatch(componentsReducerActions.COMPONENTS_LOAD_END({ components }));
            } catch (err: any) {
                console.error(err);
                dispatch(
                    componentsReducerActions.COMPONENTS_LOAD_END({
                        error: { message: err.message },
                    })
                );
            }
        };
    },
    selectComponents: (selectedComponents: string[]): ThunkResult<Promise<void>> => {
        return async (dispatch) => {
            dispatch(componentsReducerActions.SET_SELECTED_COMPONENTS(selectedComponents));
        };
    },
    unselectComponents: (selectedComponents: string[]): ThunkResult<Promise<void>> => {
        return async (dispatch) => {
            dispatch(componentsReducerActions.REMOVE_SELECTED_COMPONENTS(selectedComponents));
        };
    },
    unselectAllComponents: (): ThunkResult<Promise<void>> => {
        return async (dispatch) => {
            dispatch(componentsReducerActions.REMOVE_ALL_SELECTED_COMPONENTS());
        };
    },
    loadComponentVersions: (
        libraryName: string,
        componentName: string
    ): ThunkResult<Promise<void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const accessToken = state.auth.user?.access_token;
            dispatch(componentsReducerActions.COMPONENT_VERSIONS_LOAD_START());

            try {
                const items = await apiClient.getComponentVersions(libraryName, componentName, {
                    accessToken,
                });
                dispatch(
                    componentsReducerActions.COMPONENT_VERSIONS_LOAD_END({
                        componentVersions: items,
                    })
                );
            } catch (err: any) {
                dispatch(
                    componentsReducerActions.COMPONENT_VERSIONS_LOAD_END({
                        error: { message: err.message },
                    })
                );
            }
        };
    },

    revertComponentVersions: (
        libraryName: string,
        componentName: string | null,
        versionNumber: any
    ) => {
        return async (dispatch: any, getState: any) => {
            const state = getState();
            const accessToken = state.auth.user?.access_token;

            try {
                const results = await apiClient.revertVersion(
                    libraryName,
                    componentName,
                    versionNumber,
                    {
                        accessToken,
                    }
                );
                const revertedItem = await results.json();

                if (!isNaN(revertedItem.VersionNumber)) {
                    dispatch(componentsReducerActions.REVERTED_VERSION({}));
                }
            } catch (err: any) {
                dispatch(
                    componentsReducerActions.REVERTED_VERSION({
                        error: { message: err.message },
                    })
                );
            }
        };
    },

    setShowDeletedItems: (): ThunkResult<Promise<void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            dispatch(
                componentsReducerActions.SET_SHOW_DELETED_ITEMS(!state.components.showDeletedItems)
            );
        };
    },
};

const componentsSlice = createSlice({
    name: 'components',
    initialState: componentsDefaultState,
    reducers: {
        COMPONENT_LOAD_START(draft) {
            draft.componentLoading = true;
            draft.component = null;
            draft.componentLoadError = null;
            draft.componentSaveSucess = '';
            draft.newFolderSaveSucess = '';
        },
        COMPONENT_LOAD_END(
            draft,
            action: PayloadAction<{ component?: coreTypes.ReferenceComponent; error?: any }>
        ) {
            const { component, error } = action.payload;
            draft.componentLoading = false;
            draft.component = component || null;
            draft.componentLoadError = error;
        },
        COMPONENT_SAVE_START(draft) {
            draft.componentSaveLoading = true;
            draft.componentSaveError = null;
        },
        COMPONENT_SAVE_END(draft, action: PayloadAction<{ success?: string; error?: any }>) {
            const { success, error } = action.payload;
            draft.componentSaveLoading = false;
            draft.componentSaveSucess = success || '';
            draft.componentSaveError = error;
        },
        COMPONENTS_LOAD_START(draft) {
            draft.componentsLoading = true;
            draft.components = null;
            draft.componentsLoadError = null;
        },
        COMPONENTS_LOAD_END(
            draft,
            action: PayloadAction<{ components?: [coreTypes.ReferenceComponent]; error?: any }>
        ) {
            const { components, error } = action.payload;
            draft.componentsLoading = false;
            draft.components = components || null;
            draft.componentsLoadError = error;
            draft.revertedVersion = false;
        },
        SET_IS_COMPONENT_LOADED(draft, action: PayloadAction<boolean>) {
            draft.isComponentLoaded = action.payload;
        },
        SET_SELECTED_COMPONENTS(draft, action: PayloadAction<string[]>) {
            draft.selectedComponents = [...draft.selectedComponents, ...action.payload];
        },
        REMOVE_SELECTED_COMPONENTS(draft, action: PayloadAction<string[]>) {
            draft.selectedComponents = draft.selectedComponents.filter(
                (c) => !action.payload.includes(c)
            );
        },
        REMOVE_ALL_SELECTED_COMPONENTS(draft) {
            draft.selectedComponents = [];
        },
        COMPONENT_VERSIONS_LOAD_START(draft) {
            draft.componentVersionsLoading = true;
            draft.componentVersions = null;
            draft.componentVersionsLoadError = null;
        },
        COMPONENT_VERSIONS_LOAD_END(
            draft,
            action: PayloadAction<{ componentVersions?: any[]; error?: any }>
        ) {
            const { componentVersions, error } = action.payload;
            draft.componentVersionsLoading = false;
            draft.componentVersions = componentVersions || null;
            draft.componentVersionsLoadError = error;
            draft.revertedVersion = false;
        },
        REVERTED_VERSION(draft, action: PayloadAction<{ error?: any }>) {
            draft.revertedVersion = true;
            draft.revertedVersionError = action.payload.error;
        },
        SET_SHOW_DELETED_ITEMS(draft, action: PayloadAction<boolean>) {
            draft.showDeletedItems = action.payload;
        },
        NEWFOLDER_SAVE_START(draft) {
            draft.newFolderSaveLoading = true;
            draft.newFolderSaveError = null;
        },
        NEWFOLDER_SAVE_END(draft, action: PayloadAction<{ success?: string; error?: any }>) {
            const { success, error } = action.payload;
            draft.newFolderSaveLoading = false;
            draft.newFolderSaveSucess = success || '';
            draft.newFolderSaveError = error;
        },
    },
});

export const componentsReducerActions: any = componentsSlice.actions;
export const componentsReducer = componentsSlice.reducer;
